import * as dateFns from 'date-fns'
import * as React from 'react'

import { encodeQueryString } from '../../../app/queryString'
import { Button, Icon } from '../../components'
import { TIMEFRAME } from '../../constants/common'
import { getEarningsDateSuffix } from '../../util'
import {
  Earning,
  EarningReportReaction,
  EarningValueProperty,
  EarningsEstimateType,
  PriceReactionWhen,
} from '../earnings-common/types'
import { RsiValue } from './RsiValue'
import { EARNINGS_DATE_FORMAT, FISCAL_YEAR_MARK } from './constants'
import { PriceReactionDataEnum, PriceReactionRowDataType } from './types'

export function getIsEarningEstimate(
  earning: Pick<
    Earning,
    EarningValueProperty.SalesActual | EarningValueProperty.EpsReportedActual | EarningValueProperty.EpsActual
  >
) {
  const {
    [EarningValueProperty.SalesActual]: salesActual,
    [EarningValueProperty.EpsReportedActual]: epsReportedActual,
    [EarningValueProperty.EpsActual]: epsActual,
  } = earning

  return [salesActual, epsReportedActual, epsActual].every((value) => value === null)
}

function getChartLink(ticker: string, chartStartDate: Date, chartEndDate: Date) {
  // see: custom-date-range-utils.ts: getTimeframeFromDateRange
  const range = `range_${dateFns.format(chartStartDate, 'MM-dd-yyyy')}x${dateFns.format(chartEndDate, 'MM-dd-yyyy')}`
  const anchorDate = new Date()
  const startToReferenceDateNumOfDays = dateFns.differenceInCalendarDays(anchorDate, chartStartDate) + 1
  const timeframe = startToReferenceDateNumOfDays <= 200 ? TIMEFRAME.i30 : TIMEFRAME.d
  return `/charts?${encodeQueryString({
    t: ticker,
    p: timeframe,
    r: range,
    l: '1h1v',
  })}`
}

export function mapPriceReactionData(priceReactionData: EarningReportReaction[]) {
  const result: PriceReactionRowDataType[] = []

  for (const reportReaction of priceReactionData) {
    const earningsDate = dateFns.parseISO(reportReaction.reportDate)
    const fiscalEndDate = reportReaction.fiscalEndDate ? dateFns.parseISO(reportReaction.fiscalEndDate) : null

    const data: PriceReactionRowDataType = {
      label: reportReaction.reportDate,
      labelElement: (
        <div className="flex items-center text-default font-medium">
          <div className="pr-0.5">
            {dateFns.format(earningsDate, EARNINGS_DATE_FORMAT)} {getEarningsDateSuffix(earningsDate)}
          </div>
          <Button
            size="xsmall"
            padding="xxsmall"
            theme="link"
            as="a"
            href={getChartLink(
              reportReaction.ticker,
              dateFns.parseISO(reportReaction.chartStartDate),
              dateFns.parseISO(reportReaction.chartEndDate)
            )}
            target="_blank"
          >
            <Icon width={16} name="chartBar" />
          </Button>
        </div>
      ),
      id: reportReaction.fiscalPeriod,
      values: Object.keys(reportReaction.reactions).map((when) => ({
        year: when,
        when: when as PriceReactionWhen,
        value: reportReaction.reactions[when].price,
      })),
      subData: [
        {
          id: PriceReactionDataEnum.priceDiff,
          label: '',
          labelElement: (
            <div className="flex w-full justify-between">
              <div>
                {formatQuarterName(reportReaction.fiscalPeriod)}
                {fiscalEndDate && <span> ({dateFns.format(fiscalEndDate, 'MMM ‘yy')})</span>}
              </div>
              <div>{reportReaction.ticker} %</div>
            </div>
          ),
          values: Object.keys(reportReaction.reactions).map((when) => ({
            year: when,
            when: when as PriceReactionWhen,
            value: reportReaction.reactions[when].priceDiff,
          })),
        },
        {
          id: PriceReactionDataEnum.spyPriceDiff,
          label: '',
          labelElement: (
            <div className="flex w-full justify-between text-3xs">
              <div className="flex items-center">
                <div className="mr-1">RSI</div>
                <RsiValue className="leading-3" value={reportReaction.rsi}>
                  {reportReaction.rsi && Math.round(reportReaction.rsi)}
                </RsiValue>
              </div>
              <div>SPY %</div>
            </div>
          ),
          values: Object.keys(reportReaction.reactions).map((when) => ({
            year: when,
            when: when as PriceReactionWhen,
            value: reportReaction.reactions[when].spyPriceDiff,
          })),
        },
      ],
    }

    result.push(data)
  }

  return result
}

const QUARTAL_DELIMITER = 'Q'
const SEMI_ANNUAL_DELIMITER = 'S'

export function getIsSemiAnnualEarning(earnings: Earning[]) {
  return !!earnings[0]?.fiscalPeriod.includes(SEMI_ANNUAL_DELIMITER)
}

export function getYearAndPeriodInt(periodString: string | null, isSemiAnnualEarning: boolean) {
  const [yearStr, periodStr] =
    periodString?.split(isSemiAnnualEarning ? SEMI_ANNUAL_DELIMITER : QUARTAL_DELIMITER) ?? []
  const year = Number.parseInt(yearStr)
  const period = Number.parseInt(periodStr)

  if (isNaN(year) || isNaN(period)) {
    return { year: null, period: null }
  }

  return { year, period }
}

function getYearInt(periodString: string | null) {
  const [yearStr] = periodString?.split(FISCAL_YEAR_MARK) ?? []
  const year = Number.parseInt(yearStr)
  return isNaN(year) ? null : year
}

export function getFormattedPeriodValue(
  periodString: string | null,
  isAnnualEarning: boolean,
  isSemiAnnualEarning: boolean
) {
  if (isAnnualEarning) {
    const year = getYearInt(periodString)
    return `${FISCAL_YEAR_MARK} ${year}`
  }

  const { year, period } = getYearAndPeriodInt(periodString, isSemiAnnualEarning)
  if (year === null || period === null) {
    return null
  }

  return `${isSemiAnnualEarning ? SEMI_ANNUAL_DELIMITER : QUARTAL_DELIMITER}${period} ${year}`
}

export function formatQuarterName(fiscalPeriod: string) {
  return `${fiscalPeriod.substring(4, 6)} ‘${fiscalPeriod.substring(2, 4)}`
}

export function formatYearName(fiscalPeriod: string) {
  return `${FISCAL_YEAR_MARK} ‘${fiscalPeriod.substring(2, 4)}`
}

export function formatFiscalName(fiscalPeriod: string) {
  return fiscalPeriod.endsWith(FISCAL_YEAR_MARK) ? formatYearName(fiscalPeriod) : formatQuarterName(fiscalPeriod)
}

export function adjustEstimate(value: number, estimateType: EarningsEstimateType) {
  return estimateType === EarningsEstimateType.sales ? value * 1_000_000 : value
}
